import { useState, useEffect, useCallback, useRef } from 'react';

import { MailComposer } from '../../Components/MailComposer';
import { Design, Size } from '../../Components/designConstants';
import { useAppSelector } from '../../redux';

import { JavaLambdaFetch, GetS3SignedUrl, getCleanEmail } from '../../utils/fetch-utils';
import { useLocationCompany } from '../../utils/hooks/useLocationCompany';
import { useIsMounted } from '../../utils/hooks/useDOMHelpers';

import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useSetError, useSetAreYouSure } from '../../redux/modals';
import * as UserRedux from '../../redux/user';

import { MailerInfo, Order } from './streaming-buying-types';
import { setOrderStatus, useMailerInfo } from './use-streaming-buying';
import {
  MEDIA_OPS_EMAIL,
  makeDefaultEmailBodyText,
  orderEmailSubject,
  toEmailHtml
} from './send-order-utils';

interface OrderMailComposerProps {
  order: Order;
  network: string;
  isNewOrder?: boolean;
  // The order's properties, whose network contacts seed the recipients.
  platforms: string[];
  // Makes the IO PDF in bpm-streaming-orders and resolves its filename.
  generatePDF: () => Promise<string>;
  sending: boolean;
  setSending: (sending: boolean) => void;
  onSent: () => void;
  onClose: () => void;
}

// Emails an order's IO to the network and marks the order sent: Streaming Buying's send view and
// Plan & Buy's send dialog.
export const OrderMailComposer = ({
  order,
  network,
  isNewOrder = false,
  platforms,
  generatePDF,
  sending,
  setSending,
  onSent,
  onClose
}: OrderMailComposerProps) => {
  const getIsMounted = useIsMounted();

  const company = useLocationCompany();
  const companyInfo = useCompanyInfo();

  const sendingEmail = useAppSelector(UserRedux.sendingEmailSelector);

  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();

  useEffect(() => {
    if (order && order.status !== 0) {
      (async () => {
        try {
          await setAreYouSure({
            title: 'IO Already Sent',
            message: 'This IO has already been sent. Are you sure you want to resend it?',
            okayText: 'Yes, I want to send',
            cancelText: "I don't want to send"
          });
        } catch {
          onClose();
        }
      })();
    }
  }, [order, setAreYouSure, onClose]);

  const [filename, setFilename] = useState<string>();
  const [signedURL, setSignedURL] = useState<string>();
  // One IO per opening, even when Strict Mode runs the effect twice: each IO made for an agent
  // order replaces its send record, so the one emailed has to be the last one made.
  const generatingRef = useRef(false);
  useEffect(() => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    (async () => {
      try {
        const newFilename = await generatePDF();
        const newSignedURL = await GetS3SignedUrl(`bpm-streaming-orders/${newFilename}`);
        if (getIsMounted()) {
          setFilename(newFilename);
          setSignedURL(newSignedURL);
        }
      } catch (e) {
        setError({ message: `Couldn't make the IO. ${e.message}`, reportError: e });
        onClose();
      }
    })();
  }, [generatePDF, getIsMounted, setError, onClose]);

  const [to, setTo] = useState<string>('');
  const [cc, setCC] = useState<string[]>([]);

  const [bodyText, setBodyText] = useState('');

  const { data: mailerInfo, error: mailerError } = useMailerInfo(company ?? '', network, platforms);

  useEffect(() => {
    if (mailerError) {
      setError({
        message: `Failed to fetch mailer info. ${mailerError.message}`,
        reportError: mailerError
      });
    }
  }, [mailerError, setError]);

  // Seed the editable email fields once the contacts come back. Without a primary contact
  // or any network contacts there is nobody to send to, so the view closes itself.
  // Guarded by a ref: the deps below change identity on unrelated renders, and re-seeding
  // would discard whatever the user has typed.
  const seededMailerRef = useRef<MailerInfo | undefined>(undefined);
  useEffect(() => {
    if (!mailerInfo || seededMailerRef.current === mailerInfo) {
      return;
    }
    seededMailerRef.current = mailerInfo;
    if (!(mailerInfo.networkContacts?.length || mailerInfo.primary)) {
      setError({
        title: 'No Contacts',
        message: (
          <p>
            There are no contacts for {network} on any of the properties: {platforms.join(', ')}.
            You cannot send until there are some contacts.{' '}
            <a href={`/streaming/networks/${network}`}>You can set contacts here.</a>
          </p>
        )
      });
      onClose();
      return;
    }
    setTo(mailerInfo.primary?.email ?? '');
    setCC([
      sendingEmail,
      ...(mailerInfo.networkContacts ?? []),
      ...(mailerInfo.internalContacts ?? [])
    ]);
    setBodyText(
      makeDefaultEmailBodyText({
        name: mailerInfo.primary?.firstName ?? '',
        company: companyInfo.name,
        agency: companyInfo.agency
      })
    );
  }, [
    mailerInfo,
    network,
    platforms,
    sendingEmail,
    companyInfo.name,
    companyInfo.agency,
    setError,
    onClose
  ]);

  const [subject, setSubject] = useState(() =>
    orderEmailSubject({ order, isNewOrder, companyName: companyInfo.name, network })
  );

  const onSend = useCallback(async () => {
    setSending(true);
    const cleanTo = getCleanEmail(to);
    if (!cleanTo) {
      setError({
        title: 'Invalid Email',
        message: `"${to}" is not a valid email address`
      });
      setSending(false);
      return;
    }
    const newCC: string[] = [];
    for (const email of cc) {
      if (!email) {
        continue;
      }
      // remove all whitespace
      const cleanEmail = getCleanEmail(email);
      if (!cleanEmail) {
        setError({
          title: 'Invalid Email',
          message: `"${email}" is not a valid email address`
        });
        setSending(false);
        return;
      }
      newCC.push(cleanEmail);
    }
    newCC.push(MEDIA_OPS_EMAIL);

    try {
      await JavaLambdaFetch('/send_mail', {
        method: 'POST',
        body: {
          to: cleanTo,
          agency: companyInfo.agency,
          cc: newCC.join(', '),
          subject,
          body: toEmailHtml(bodyText),
          attachment: `s3://bpm-streaming-orders/${filename}`
        }
      });
    } catch (e) {
      setError({ message: `Failed to send PDF. ${e.message}`, reportError: e });
      setSending(false);
      return;
    }
    if (order.status < 11) {
      try {
        await setOrderStatus(order.id, 11);
      } catch (e) {
        setError({
          message: `The IO was emailed, but order ${order.id} couldn't be marked sent. ${e.message}`,
          reportError: e
        });
      }
    }
    onSent();
    setSending(false);
    onClose();
  }, [
    setSending,
    setError,
    cc,
    bodyText,
    to,
    filename,
    onSent,
    onClose,
    companyInfo,
    order,
    subject
  ]);

  return (
    <MailComposer
      loaded={Boolean(mailerInfo)}
      to={to}
      onToChange={setTo}
      cc={cc}
      onCCChange={setCC}
      readOnlyCC={[MEDIA_OPS_EMAIL]}
      subject={subject}
      onSubjectChange={setSubject}
      bodyText={bodyText}
      onBodyTextChange={setBodyText}
      onCancel={onClose}
      onSend={onSend}
      sendDisabled={!filename}
      sendLoading={sending}
      previewUrl={signedURL}
      buttonSize={Size.MD}
      cancelButtonDesign={Design.NEUTRAL}
      addCcButtonClassName='w-100'
    />
  );
};
